// Entraînement › « Ma semaine » (refonte navigation 21/10, spec ARBORESCENCE-ACS §3 + D9). ONE agenda, week by week:
// 1 · the validated large-numeral strip (AgendaV3 look, same .cc-home-v3 classes) for the current and the next week, with the
//     shared selected day (store.planningDay, also read by Réserver and the home);
// 2 · the day's small-group slots, booked AND available, each with ITS séance of the small-group programmation (GROUP_SESSIONS,
//     theme + exercises). Tap on a slot → Réserver (confirmation, credits, waitlist, cancellation unchanged); tap on its séance →
//     a sheet (theme, exercises linked to their bank fiche, the slot's action through Réserver);
// 3 · « Mes séances perso » when the member has a personal programmation that week: dated sessions or « Sans horaire imposé »,
//     with their existing Commencer / Reprendre route (Séance player) and the programmation page.
// Bookings never count as done; a done personal session shows « Terminée » only from realised records (statusOf).
import { FilDuJour, type FilExtra } from "./fil-du-jour";
import { Button } from "@ubm/ui/components/button";
import { useRef, useState, type KeyboardEvent } from "react";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { Avatar } from "@ubm/ui/components/avatar";
import { Progress } from "@ubm/ui/components/progress";
import { CustomSheet } from "./sheet";
import { TODAY_KEY, groupSessionOf, type GroupSession, type Slot } from "./home-fixtures";
import { SLOT_EXTRAS } from "./reserver-fixtures";
import { capitalize } from "./demo-context";
import { hoursBefore, isoAdd, isoOfDayKey, store, useStore } from "./store";
import { CANONICAL_ID, countedProgs, statusOf, useProgState, weekLabel, type ProgSession, type Programmation } from "./programmations-state";
import { TEAM } from "./studio-fixtures";
import { CATALOGUE } from "./banque-fixtures";

/** Weeks of the agenda: the current one and the next (the slots of the store cover these two). */
const WEEK_MONDAYS = ["2026-10-12", "2026-10-19"];
const keyOf = (iso: string) => `d${Number(iso.slice(8))}`;
const dateOf = (iso: string) => new Date(`${iso}T12:00:00`);
const daysOf = (monday: string) => Array.from({ length: 7 }, (_, i) => {
  const iso = isoAdd(monday, i);
  return { key: keyOf(iso), iso, letter: "LMMJVSD"[i]!, num: dateOf(iso).getDate(), long: dateOf(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }) };
});
const shortDay = (iso: string) => capitalize(dateOf(iso).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" }).replace(".", ""));
const themeOf = (s: Slot) => s.title.replace(/^Small group · /, "");
const coachName = (g?: GroupSession, s?: Slot) => (SLOT_EXTRAS[s?.id ?? ""]?.coach ?? (g ? TEAM[g.coach === "antoine" ? 1 : 0]?.firstName : undefined))?.split(" ")[0];
const slugOf = (name: string) => name.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const ficheOf = (name: string) => CATALOGUE.find(x => x.kind === "exercice" && x.id === slugOf(name));
const launch = (sid: string) => { window.location.hash = sid === CANONICAL_ID ? "#/seance" : `#/seance?id=${sid}`; };
const openSlot = (s: Slot) => { window.location.hash = `#/reserver?jour=${s.day}&creneau=${s.id}&retour=entrainement`; };

export function MaSemaine() {
  const shared = useStore();
  useProgState();
  const [sheet, setSheet] = useState<{ slot: Slot; g: GroupSession } | null>(null);
  const field = useRef<HTMLDivElement>(null);
  const weekIndex = Math.max(0, WEEK_MONDAYS.findIndex(m => daysOf(m).some(d => d.key === shared.planningDay)));
  const monday = WEEK_MONDAYS[weekIndex]!;
  const days = daysOf(monday);
  const day = days.find(d => d.key === shared.planningDay) ?? days.find(d => d.key === TODAY_KEY) ?? days[0]!;
  const index = days.indexOf(day);
  const goWeek = (i: number) => {
    const target = daysOf(WEEK_MONDAYS[i]!);
    store.setPlanningDay(target.some(d => d.key === TODAY_KEY) ? TODAY_KEY : target[0]!.key);
  };

  const waiting = (s: Slot) => shared.waitlist.includes(s.id);
  const past = (s: Slot) => hoursBefore(s) <= 0;
  const slotsOf = (key: string) => shared.slots.filter(s => s.day === key).sort((a, b) => a.time.localeCompare(b.time));
  const progs = countedProgs(monday);
  const perso = progs.flatMap(p => p.sessions.map(s => ({ p, s, st: statusOf(p, s) })));
  const marked = new Set(days.filter(d => slotsOf(d.key).some(s => s.state === "booked" || waiting(s)) || perso.some(x => x.s.date === d.iso)).map(d => d.key));
  const daySlots = slotsOf(day.key);
  const dayPerso = perso.filter(x => x.s.date === day.iso);

  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    const btns = [...(field.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    const i = btns.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) return;
    const to = e.key === "ArrowLeft" ? i - 1 : e.key === "ArrowRight" ? i + 1 : e.key === "Home" ? 0 : e.key === "End" ? btns.length - 1 : null;
    if (to === null) return;
    e.preventDefault();
    btns[Math.max(0, Math.min(btns.length - 1, to))]?.focus();
  };

  const state = (s: Slot): { label: string; tone: "cta" | "booked" | "muted" | "wait" } =>
    past(s) ? { label: s.state === "booked" ? "Réservé" : "Passé", tone: "muted" }
    : s.state === "booked" ? { label: "Réservé", tone: "booked" }
    : s.state === "full" ? (s.waitlist ? { label: waiting(s) ? "En attente" : "Liste d'attente", tone: "wait" } : { label: "Complet", tone: "muted" })
    : { label: "Réserver", tone: "cta" };

  // Fil du jour (21/10) : ce que JE fais ce jour-là (cours réservés + séances perso datées), calendrier de la page sur deux
  // semaines. Dessous, « Réserver un cours » : seulement les créneaux encore à prendre (les réservés vivent dans le fil).
  const perDay = (key: string): FilExtra[] => perso.filter(x => x.s.date && keyOf(x.s.date) === key).map(x => ({
    id: `pp-${x.s.id}`, h: null, hh: "Perso", label: x.s.title.split(" · ").pop()!, kick: `Séance perso · ${x.p.name}`, title: x.s.title,
    text: `${x.s.minutes} min${x.s.exercises?.length ? "" : " · le détail arrive de ton coach"}`, done: x.st === "done",
    act: x.st !== "done" && x.s.exercises?.length ? (x.st === "started" ? "Reprendre" : "Commencer") : undefined, onAct: () => launch(x.s.id) }));
  const toBook = daySlots.filter(s => s.state !== "booked" && !past(s));
  const reserve = useRef<HTMLElement>(null);

  return <div className="cc-ent-semaine">
    <section className="hub-fil" aria-labelledby="ent-fil"><h2 id="ent-fil" className="hub-acc-h">Mes séances</h2>
      <FilDuJour domains={["entrainement"]} day={day.key} onDay={k => store.setPlanningDay(k)} days={days}
        weekLabel={weekIndex === 0 ? "cette semaine" : `semaine du ${days[0]!.num}`}
        onWeek={{ prev: weekIndex > 0 ? () => goWeek(weekIndex - 1) : undefined, next: weekIndex < WEEK_MONDAYS.length - 1 ? () => goWeek(weekIndex + 1) : undefined }}
        extra={perDay} emptyCta={{ label: "Voir les cours du jour", onClick: () => reserve.current?.scrollIntoView({ behavior: "smooth", block: "start" }) }} /></section>

    <section ref={reserve} className="cc-ent-block cc-ent-book" aria-labelledby="ent-book">
      <div className="cc-sectionhead"><h2 id="ent-book">Réserver un cours</h2>
        <button type="button" className="cc-seeall" onClick={() => { window.location.hash = "#/reservations"; }}>Mes réservations <ChevronRight size={13} strokeWidth={2.2} /></button></div>
      <div role="region" aria-live="polite" aria-label={capitalize(day.long)}>
        {/* Billets (variante R1 validée 21/10) : l'heure en grand, la perforation, les places restantes ; titre → la séance. */}
        {toBook.length ? <ul className="cc-ent-tickets">
          {toBook.map(s => {
            const g = groupSessionOf(s), st = state(s), coach = coachName(g, s), places = SLOT_EXTRAS[s.id]?.places;
            const photo = TEAM.find(t => t.firstName.startsWith(coach ?? "-"))?.photo;
            return <li key={s.id} className="cc-ent-tk" data-full={s.state === "full" || undefined}>
              <div className="cc-ent-tk-t"><b>{s.time}</b><span>{s.minutes} min</span></div>
              <div className="cc-ent-tk-b">
                <button type="button" className="cc-ent-tk-h" onClick={() => g ? setSheet({ slot: s, g }) : openSlot(s)} aria-label={`Séance ${themeOf(s)}`}>
                  {photo ? <img src={photo} alt="" /> : <span className="cc-ent-tk-av" aria-hidden="true">{(coach ?? "A")[0]}</span>}
                  <span><b>{themeOf(s)}</b><small>Small group{coach ? ` · ${coach}` : ""}</small></span>
                </button>
                <div className="cc-ent-tk-f">
                  <span className="cc-ent-seats">{s.state === "full" ? "Complet" : places !== undefined ? <>{Array.from({ length: 8 }, (_, i) => <i key={i} data-f={i < 8 - places || undefined} />)}<em>{places} place{places > 1 ? "s" : ""}</em></> : "Places disponibles"}</span>
                  <Button variant={st.tone === "cta" ? "primary" : "secondary"} size="sm" className="cc-ent-tk-cta" onClick={() => openSlot(s)}>{st.label}</Button>
                </div>
              </div>
            </li>;
          })}
        </ul> : <p className="cc-home-empty">{daySlots.some(s => !past(s)) ? "Tu es déjà inscrit à tous les cours de ce jour." : daySlots.length ? "Plus de cours à réserver ce jour." : "Pas de cours ce jour."}</p>}
      </div>
    </section>

    {progs.length > 0 && <section className="cc-ent-block cc-ent-perso" aria-labelledby="ent-perso">
      <div className="cc-sectionhead"><h2 id="ent-perso">Mes séances perso</h2>
        <button type="button" className="cc-seeall" onClick={() => { window.location.hash = `#/programmations?semaine=${monday}`; }}>Programmation <ChevronRight size={13} strokeWidth={2.2} /></button></div>
      {progs.map(p => <PersoProg key={p.id} p={p} items={perso.filter(x => x.p.id === p.id)} />)}
    </section>}

    {sheet && <SeanceSheet slot={shared.slots.find(x => x.id === sheet.slot.id) ?? sheet.slot} g={sheet.g} waiting={waiting(sheet.slot)} past={past(sheet.slot)} onClose={() => setSheet(null)} />}
  </div>;
}

// Programme en carte encre (variante P1 validée 21/10) : coach, progression en segments, séances lettrées ; la prochaine à
// faire est mise en avant avec « Commencer » en or. Les routes Commencer / Reprendre (Séance player) sont inchangées.
function PersoProg({ p, items }: { p: Programmation; items: { p: Programmation; s: ProgSession; st: ReturnType<typeof statusOf> }[] }) {
  const coach = TEAM[p.coach === "antoine" ? 1 : 0];
  const done = items.filter(x => x.st === "done").length;
  const cur = items.find(x => x.st !== "done" && x.s.exercises?.length);
  return <div className="cc-ent-p1">
    <div className="cc-ent-p1-h">
      <div className="cc-ent-p1-top">
        {coach && <img src={coach.photo} alt="" />}
        <span><b>{p.name}</b><small>{p.theme} · avec {coach?.firstName.split(" ")[0] ?? "ton coach"}</small></span>
        <em><b>{done}</b>/{items.length}</em>
      </div>
      <div className="cc-ent-p1-segs" role="progressbar" aria-valuemin={0} aria-valuemax={items.length} aria-valuenow={done} aria-label={`Séances terminées de ${p.name}`}>
        {items.map((x, i) => <i key={x.s.id} data-f={x.st === "done" || undefined} style={{ animationDelay: `${i * 120}ms` }} />)}</div>
    </div>
    <ul className="cc-ent-p1-l">{items.map((x, i) => {
      const letter = x.s.title.match(/^Séance ([A-Z])/)?.[1] ?? String.fromCharCode(65 + i);
      const title = x.s.title.split(" · ").pop()!;
      const playable = !!x.s.exercises?.length && x.st !== "done";
      const when = x.s.date ? shortDay(x.s.date) : "Sans horaire imposé";
      return <li key={x.s.id} data-done={x.st === "done" || undefined} data-cur={cur?.s.id === x.s.id || undefined}>
        <span className="cc-ent-p1-n" aria-hidden="true">{x.st === "done" ? <Check size={15} strokeWidth={3} /> : letter}</span>
        <span className="cc-ent-p1-t"><b>{title}</b><small>{when} · {x.st === "done" ? "terminée" : `${x.s.minutes} min`}</small>
          {!x.s.exercises?.length && x.st !== "done" && <small>Le détail arrive de ton coach.</small>}</span>
        {playable && (cur?.s.id === x.s.id
          ? <Button variant="primary" size="sm" className="cc-ent-p1-cta" onClick={() => launch(x.s.id)}>{x.st === "started" ? "Reprendre" : "Commencer"}</Button>
          : <button type="button" className="cc-ent-p1-chip" onClick={() => launch(x.s.id)}>{x.st === "started" ? "Reprendre" : "Commencer"}</button>)}
      </li>;
    })}</ul>
  </div>;
}

/** A personal session: day (or « Sans horaire imposé »), title, Terminée / Commencer / Reprendre. Launch guard: no exercise shared →
 *  nothing to play (same copy as Mes programmations). */
function PersoRow({ p, s, st, tag }: { p: Programmation; s: ProgSession; st: ReturnType<typeof statusOf>; tag?: boolean }) {
  const playable = !!s.exercises?.length && st !== "done";
  const body = <>
    <span className="cc-ent-time cc-ent-time--perso">{tag ? "Perso" : s.date ? shortDay(s.date) : ""}</span>
    <span className="cc-ent-slot-text"><b>{s.title}</b><small>{tag ? `${p.name} · ` : s.date ? "" : "Sans horaire imposé · "}{s.minutes} min</small>
      {!s.exercises?.length && st !== "done" && <small>Le détail des exercices n'est pas encore partagé par ton coach.</small>}</span>
    {st === "done" ? <span className="cc-ent-state" data-tone="done"><Check size={13} strokeWidth={2.6} aria-hidden="true" />Terminée</span>
      : playable ? <span className="cc-ent-state" data-tone="cta">{st === "started" ? "Reprendre" : "Commencer"}</span> : null}
  </>;
  return <li className="cc-ent-slot cc-ent-slot--perso">
    {playable ? <button type="button" className="cc-ent-slot-main" onClick={() => launch(s.id)}>{body}</button> : <div className="cc-ent-slot-main">{body}</div>}
  </li>;
}

function SeanceSheet({ slot, g, waiting, past, onClose }: { slot: Slot; g: GroupSession; waiting: boolean; past: boolean; onClose: () => void }) {
  const coach = TEAM[g.coach === "antoine" ? 1 : 0];
  const when = `${capitalize(dateOf(isoOfDayKey(slot.day)).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" }))} · ${slot.time} · ${slot.minutes} min`;
  const cta = past ? null : slot.state === "booked" ? "Gérer ma réservation" : slot.state === "open" ? "Réserver ce créneau"
    : slot.waitlist ? (waiting ? "Voir ma place en liste d'attente" : "Rejoindre la liste d'attente") : null;
  return <CustomSheet title={`Séance ${g.theme}`} onClose={onClose} overlayClassName="cc-home-overlay" className="cc-home-sheet cc-ent-sheet">
    <p className="cc-ent-sheet-when">{when}</p>
    <p className="cc-ent-sheet-state" data-booked={slot.state === "booked" || undefined}>{slot.state === "booked" ? <><Check size={14} strokeWidth={2.6} aria-hidden="true" /> Ta place est réservée</> : past ? "Cours passé" : "Small group au studio"}</p>
    <p className="cc-home-p">{g.focus}</p>
    <ol className="cc-ent-ex">{g.exercises.map((x, i) => { const f = ficheOf(x); return <li key={x}>
      {f ? <button type="button" onClick={() => { window.location.hash = `#/banque?fiche=${f.id}`; }}><span>{i + 1}</span><b>{x}</b><ChevronRight size={14} aria-hidden="true" /></button>
        : <div><span>{i + 1}</span><b>{x}</b></div>}
    </li>; })}</ol>
    {coach && <p className="cc-ent-sheet-coach"><Avatar name={coach.firstName} src={coach.photo} size="sm" /> Programmation small group de {coach.firstName.split(" ")[0]}</p>}
    {cta && <Button variant="primary" size="lg" className="cc-cta cc-ent-sheet-cta" onClick={() => openSlot(slot)}>{cta}</Button>}
    {!cta && !past && <p className="cc-home-demo">Ce cours est complet.</p>}
    <p className="cc-home-demo">Prototype : programmation de démonstration, aucune réservation réelle.</p>
  </CustomSheet>;
}

